import React from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * Touch-friendly Input Component
 * Ensures minimum 48px height for mobile
 */
const TouchInput = React.forwardRef(({ 
  label, 
  error, 
  required, 
  type = 'text',
  fullWidth = false,
  ...props 
}, ref) => {
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        gridColumn: fullWidth ? 'span 2' : 'span 1',
        marginBottom: '4px'
      }}
    >
      {label && (
        <label
          style={{
            display: 'block',
            color: 'var(--text-primary)',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            marginBottom: '8px'
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--gold)', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        {...props}
        style={{
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: '12px 16px',
          fontSize: 'var(--font-size-base)',
          background: 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          outline: 'none',
          transition: 'all 0.2s ease',
          ...props.style
        }}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--gold)';
          e.target.style.background = 'rgba(212, 175, 55, 0.05)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? 'var(--error)' : 'var(--border)';
          e.target.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      />
      {error && (
        <div
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: 'var(--error)'
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
});

/**
 * Touch-friendly Button Component
 */
export const TouchButton = ({ 
  children, 
  variant = 'primary', 
  fullWidth = false,
  loading = false,
  ...props 
}) => {
  const isMobile = useIsMobile();

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          background: 'var(--gold)',
          color: 'var(--bg-base)',
          border: 'none',
          boxShadow: 'var(--shadow-gold)'
        };
      case 'secondary':
        return {
          background: 'transparent',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)'
        };
      case 'danger':
        return {
          background: 'var(--error)',
          color: '#fff',
          border: 'none'
        };
      default:
        return {};
    }
  };

  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      style={{
        width: fullWidth ? '100%' : 'auto',
        minHeight: 'var(--touch-target-min)',
        padding: isMobile ? '14px 24px' : '12px 24px',
        fontSize: isMobile ? '15px' : '16px',
        fontWeight: '700',
        borderRadius: 'var(--radius-md)',
        cursor: loading || props.disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: loading || props.disabled ? 0.6 : 1,
        ...getVariantStyles(),
        ...props.style
      }}
      onTouchStart={(e) => {
        if (!loading && !props.disabled) {
          e.currentTarget.style.transform = 'scale(0.97)';
        }
      }}
      onTouchEnd={(e) => {
        if (!loading && !props.disabled) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      {loading ? '⏳ Ładowanie...' : children}
    </button>
  );
};

/**
 * Touch-friendly Select Component
 */
export const TouchSelect = ({ label, error, required, options, fullWidth = false, ...props }) => {
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        gridColumn: fullWidth ? 'span 2' : 'span 1',
        marginBottom: '4px'
      }}
    >
      {label && (
        <label
          style={{
            display: 'block',
            color: 'var(--text-primary)',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            marginBottom: '8px'
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--gold)', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <select
        {...props}
        style={{
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: '12px 16px',
          fontSize: 'var(--font-size-base)',
          background: 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          outline: 'none',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          ...props.style
        }}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--gold)';
          e.target.style.background = 'rgba(212, 175, 55, 0.05)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? 'var(--error)' : 'var(--border)';
          e.target.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: 'var(--error)'
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default TouchInput;
